import React, { useEffect, useState } from 'react';

import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Web, Home } from '@mui/icons-material';
import { Divider, Tooltip } from '@mui/material';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import CustomerItem from './CustomerItem';
import {
  getClient,
  getUserData,
  getAllCustomersForUser,
  updateUser,
  createUserCustomer,
  updateUserCustomer,
  deleteUserCustomer,
} from './operations';
import { getAMCClients } from '../AddClientUser/operations';
import styles from './styles';
import { Button, SubHeader, InputBox, ModalConfirmation, Footer } from '../../../components';
import useIsMobileScreen from '../../../hooks/useCheckIsMobile';
import getRandomId from '../../../utils/getRandomId';
import { getListDuplicates } from '../../../utils/listDuplicates';

import './styles.scoped.css';

const chunkAllSettled = async (items, asyncFn, batchSize = 10) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(item => asyncFn(item)));
    results.push(...settled);
  }
  return results;
};

const UpdateClientUser = () => {
  const navigation = useNavigate();
  const { id, user } = useParams();
  const [clientId, setClientId] = useState();
  const [clientData, setClientData] = useState();
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState();
  const [userData, setUserData] = useState();
  const [clients, setClients] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const isMobile = useIsMobileScreen();
  const [customers, setCustomers] = useState([]);
  const [loadedCustomers, setLoadedCustomers] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchClient = async () => {
    if (!clientId || clientId === '') return;
    setLoading(true);
    setLoadFailed(false);
    setSelectedContact(null);
    const response = await getClient(clientId);
    if (response.response_code === 200) {
      const status = response.response_body?.clientStatus?.split(' ')[0];
      const statuses = process.env.REACT_APP_CLIENT_STATUSES.split(',');
      if (statuses.includes(status)) {
        setClientData(response.response_body);
        if (response?.response_body?.clientAccountMasterCode !== " ") {
          try {
            const res = await getAMCClients(response?.response_body?.clientAccountMasterCode);
            setClients(res?.response_body);
          } catch (e) {
            // non-critical — AMC sibling list unavailable, continue with single client
          }
        }
        // loading stays true — fetchUserData (triggered via useEffect) will clear it
      } else {
        setTitle('Invalid');
        setMessage('Client not active in Ultra Staff');
        setLoading(false);
      }
    } else {
      setClientId(null);
      setClientData(null);
      setSelectedContact(null);
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    setLoading(true);

    const newUserData = await getUserData(user);
    if (!newUserData) {
      setLoading(false);
      return false;
    }

    const { items: allItems, complete } = await getAllCustomersForUser(user);
    setLoadFailed(!complete);

    if (allItems.length === 0) {
      setCustomers([
        { customerId: '', clientName: '', clientStatus: '', active: false, isValid: false, id: 0, error: '' },
      ]);
      setLoadedCustomers([]);
      setUserData(newUserData);
      setLoading(false);
      return complete;
    }

    const settledResults = await chunkAllSettled(allItems, item => getClient(item.customerId));

    const newCustomers = settledResults
      .map((result, index) => {
        const item = allItems[index];
        if (result.status === 'fulfilled' && result.value?.response_code === 200) {
          return {
            customerId: item.customerId,
            clientName: result.value.response_body.clientName,
            clientStatus: result.value.response_body.clientStatus,
            active: item.active,
            isValid: true,
            id: item.id,
            error: '',
          };
        }
        return {
          customerId: item.customerId,
          clientName: 'Unable to load',
          clientStatus: 'Unknown',
          active: item.active,
          isValid: false,
          id: item.id,
          error: 'Client lookup failed',
        };
      })
      .sort((a, b) => Number(a.customerId) - Number(b.customerId));

    setCustomers(newCustomers);
    setLoadedCustomers(allItems);
    setUserData(newUserData);
    setLoading(false);
    return complete;
  };

  const update = async () => {
    if (loadFailed) {
      setTitle('Load Error');
      setMessage('Customer list could not be fully loaded. Please refresh the page before saving.');
      return;
    }

    const erroredCustomer = customers.find(c => !!c.error);
    if (erroredCustomer) {
      setTitle('Invalid');
      setMessage(`Please, check clients. There is an error: ${erroredCustomer.error}`);
      return;
    }

    setSaveLoading(true);

    try {
      const userInput = {
        id: userData?.id,
        customerId: clientId,
        clientName: clientData?.clientName,
        name: [selectedContact?.firstName, selectedContact?.lastName].filter(Boolean).join(' ') || userData?.name || '',
      };
      if (selectedContact?.smsOptIn === true && selectedContact?.cellPhone) {
        userInput.cellPhone = selectedContact.cellPhone.replace(/\D/g, '');
      }
      const response = await updateUser(userInput);

      if (!response || response?.errors?.length) {
        setSaveLoading(false);
        setTitle('Error');
        setMessage(response?.errors?.[0]?.message ?? 'Failed to update user. Please try again.');
        return;
      }

      const loadedById = new Map(loadedCustomers.map(lc => [lc.id, lc]));
      const formIds = new Set(customers.map(c => c.id));

      const toDelete = loadedCustomers.filter(lc => !formIds.has(lc.id));
      const toCreate = customers.filter(c => !loadedById.has(c.id) && c.isValid);
      const toUpdate = customers.filter(c => {
        const original = loadedById.get(c.id);
        if (!original) return false;
        return original.active !== c.active || Number(original.customerId) !== Number(c.customerId);
      });

      const mutations = [
        ...toDelete.map(lc => () => deleteUserCustomer({ id: lc.id })),
        ...toCreate.map(c => () => createUserCustomer({ active: c.active, customerId: c.customerId, userCustomersId: user })),
        ...toUpdate.map(c => () => updateUserCustomer({ id: c.id, active: c.active, customerId: c.customerId })),
      ];

      const mutationResults = await chunkAllSettled(mutations, fn => fn());
      const hasMutationFailure = mutationResults.some(r => r.status === 'rejected' || r.value === false);

      // Re-fetch so newly created rows get their real DynamoDB ids in loadedCustomers,
      // ensuring a second Save in the same session computes the diff correctly.
      const refetchComplete = await fetchUserData();

      setSaveLoading(false);
      if (hasMutationFailure) {
        setTitle('Partial Save');
        setMessage('Some changes could not be saved. Please refresh and verify your changes.');
      } else if (!refetchComplete) {
        setTitle('Partial Save');
        setMessage('Changes saved, but page could not fully reload. Please refresh before making further edits.');
      } else {
        setTitle('Success');
        setMessage('The user has been successfully updated.');
      }
    } catch (error) {
      setSaveLoading(false);
      setTitle('Error');
      setMessage('An unexpected error occurred. Please try again.');
    }
  };

  const newRow = () => {
    setCustomers([
      ...customers,
      {
        customerId: '',
        clientName: '',
        clientStatus: '',
        active: false,
        isValid: false,
        id: getRandomId(),
        error: '',
      },
    ]);
  };

  useEffect(() => {
    if (id) setClientId(id);
  }, [id]);

  useEffect(() => {
    if (clientId) fetchClient();
  }, [clientId]);

  useEffect(() => {
    if (clientData && user) {
      const contact = clientData.clientContacts.find(item => item.emailAddress === user);
      if (contact) {
        setSelectedContact(contact);
        // loading stays true — fetchUserData (triggered by selectedContact change) will clear it
      } else {
        setLoading(false);
        setTitle('Not Found');
        setMessage('Client contact not found in Ultra Staff');
      }
    }
  }, [clientData, user]);

  useEffect(() => {
    if (selectedContact) fetchUserData();
  }, [selectedContact]);

  useEffect(() => {
    const duplicatedValues = getListDuplicates(customers.filter(c => c.customerId), 'customerId');
    const newList = [...customers];
    let shouldUpdate = false;
    for (let i = 0; i < customers.length; i++) {
      const customer = customers[i];
      const newErrorValue = duplicatedValues.find(dv => dv.customerId === customer.customerId)
        ? 'Client Already Assigned'
        : '';
      // If customer error was Client Already Assigned or empty and now it's different, update the error
      if ((customer.error === 'Client Already Assigned' || !customer.error) && customer.error !== newErrorValue) {
        shouldUpdate = true;
        const indexToChange = newList.findIndex(c => c.id === customer.id);
        newList[indexToChange] = {
          ...newList[indexToChange],
          isValid: !newErrorValue,
          error: newErrorValue,
        };
      }
    }
    if (shouldUpdate) setCustomers(newList);
  }, [customers]);

  return (
    <div>
      <SubHeader
        buttons={[
          { icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation('/dashboard') },
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation(-1) },
        ]}
        text="Admin Portal"
      />
      <div className="wrap">
        <div className="inner" style={{ width: isMobile ? '100%' : '70%' }}>
          <div className="bar">Update Client User</div>
          <div className="content">
            <Form>
              <Row>
                <Form.Label style={{ margin: 15, marginBottom: 30 }}>
                  Information displayed is from Ultra Staff. Update Ultra Staff first then click refresh{' '}
                  <FontAwesomeIcon icon={faRedo} className="fa-sm" /> below to display changes in DQF. Click Save to
                  retain those updates.
                </Form.Label>
              </Row>
              <Row>
                <Col>
                  <Form.Label>User</Form.Label>
                  <div className="label">{user}</div>
                </Col>
                <Col>
                  <Form.Label>Client Name</Form.Label>
                  <div className="label">{clientData?.clientName}</div>
                </Col>
                <Col>
                  <Form.Label>Client Status</Form.Label>
                  <div className="label">{clientData?.clientStatus}</div>
                </Col>
                <div style={{ width: 96 }} />
              </Row>
              {selectedContact && (
                <Row>
                  <Col style={{ padding: 0 }}>
                    <div className="box" style={{ padding: 0 }}>
                      <Row style={{ padding: 16 }}>
                        <Col>
                          <InputBox disabled title="First Name" value={selectedContact?.firstName} />
                          <InputBox style={styles.marginTop} disabled title="AMC Code" value={clientData?.clientAccountMasterCode} />
                        </Col>
                        <Col>
                          <InputBox disabled title="Last Name" value={selectedContact?.lastName} />
                          <InputBox
                            disabled
                            style={styles.marginTop}
                            title="Ultra Staff Contact Status"
                            value={selectedContact?.status}
                          />
                        </Col>
                        <Col>
                          <InputBox
                            disabled
                            title="Cell Phone"
                            value={selectedContact?.smsOptIn === true ? selectedContact?.cellPhone : ''}
                            error={
                              !!selectedContact &&
                              !!userData &&
                              selectedContact?.cellPhone?.replace(/\D/g, '') !== userData?.cellPhone?.replace(/\D/g, '')
                            }
                            errorMessage={
                              selectedContact?.smsOptIn === true
                                ? `${selectedContact?.cellPhone} does not match ${userData?.cellPhone}`
                                : ''
                            }
                          />
                          <InputBox
                            disabled
                            style={styles.marginTop}
                            title="SMS Opt-in"
                            value={selectedContact?.smsOptIn ? 'Yes' : 'No'}
                            errorMessage="SMS Opt In Not Set"
                            error={typeof selectedContact?.smsOptIn !== 'boolean'}
                          />
                        </Col>
                        <div style={{ display: 'flex', justifyContent: 'start', width: 96 }}>
                          <Tooltip title="Refresh data from Ultra Staff" arrow>
                            <Button variant="primary" disabled={loading} style={{ height: 38, alignSelf: 'flex-end', marginBottom: 6 }} onPress={fetchClient}>
                              <FontAwesomeIcon icon={faRedo} className="fa-lg" />
                            </Button>
                          </Tooltip>
                        </div>
                      </Row>
                      <Divider />
                      {loading && (
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 152,
                          }}>
                          <Spinner animation="border" size="xl" />
                        </div>
                      )}
                      {!loading && !!customers.length && (
                        <Row style={{ padding: 16, paddingBottom: 0 }}>
                          <Col>
                            <Col>
                              <Form.Label style={{ paddingLeft: 40 }}>Client ID</Form.Label>
                            </Col>
                          </Col>
                          <Col>
                            <Col>
                              <Form.Label>Client Name</Form.Label>
                            </Col>
                          </Col>
                          <Col>
                            <Col>
                              <Form.Label>Client Status</Form.Label>
                            </Col>
                          </Col>
                          <div style={{ width: 96 }}>
                            <Form.Label>Active</Form.Label>
                          </div>
                        </Row>
                      )}
                      {!loading &&
                        customers.map(customer => (
                          <CustomerItem
                            customer={customer}
                            customers={customers}
                            setCustomers={setCustomers}
                            key={customer.id}
                          />
                        ))}
                      <Row style={{ padding: 16 }}>
                        <Col>
                          {!loading && (
                            <Tooltip title={customers.find(c => !c.isValid) ? 'Fix or remove invalid rows before adding another' : 'Add customer'} arrow>
                              <span>
                                <Button
                                  variant="primary"
                                  disabled={!!customers.find(customer => !customer.isValid)}
                                  style={styles.plus}
                                  onPress={newRow}
                                  text="+"
                                />
                              </span>
                            </Tooltip>
                          )}
                        </Col>
                        <Col />
                        <Col />
                        <div style={{ width: 96 }} />
                      </Row>
                      <Row style={{ padding: 16 }}>
                        <Col />
                        <Col>
                          <Button
                            variant="primary"
                            fullWidth
                            style={styles.buttonPrimary}
                            onPress={update}
                            disabled={saveLoading || loading}>
                            {saveLoading ? 'Saving...' : 'Save'}
                          </Button>
                        </Col>
                        <Col />
                      </Row>
                    </div>
                  </Col>
                </Row>
              )}
            </Form>
          </div>
        </div>
      </div>
      <ModalConfirmation
        title={title}
        body={message}
        primaryButtonTitle="Add Another"
        secondaryButtonTitle="Close"
        isOpen={title.length > 0}
        onClick={() => {
          setClientId('');
          setTitle('');
        }}
        secondaryOnClick={() => {
          if (title === 'Invalid' || title === 'Partial Save' || title === 'Load Error' || title === 'Error') {
            setTitle('');
            setMessage('');
          } else {
            navigation(-1);
          }
        }}
        optionalClose
      />
      <Footer />
    </div>
  );
};

export default UpdateClientUser;
